// src/components/filter/UnitFilter.js
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { IconChevronDown, IconX, IconCheck } from '@tabler/icons-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import leaderIcon from '../../assets/gptw_icons/Icons_rot/user-02.png';
import './UnitFilter.css';

const buildUnitTree = (units) => {
    const tree = [];
    const map = {};

    // Key by unit_name to match parent_unit reference shape
    units.forEach((unit) => {
        map[unit.unit_name] = { ...unit, children: [] };
    });

    units.forEach((unit) => {
        if (unit.parent_unit && map[unit.parent_unit]) {
            map[unit.parent_unit].children.push(map[unit.unit_name]);
        } else {
            tree.push(map[unit.unit_name]);
        }
    });

    return tree;
};

/**
 * Flatten the unit tree into options with depth information for indentation.
 * @param {Array} nodes - Tree nodes to flatten
 * @param {number} depth - Current depth level
 * @param {string} visibilityMode - 'frontend' (visibility 1 or 2), 'dashboard' (visibility 0 or 2), or 'all'
 * @param {boolean} flatMode - If true, all items will have depth 0 (no hierarchy indentation)
 * @param {boolean} disableLeafUnits - If true, units with no children (leaf units) are disabled
 */
const flattenTreeToOptions = (nodes, depth = 0, visibilityMode = 'frontend', flatMode = false, disableLeafUnits = false) => {
    const options = [];
    nodes.forEach((node) => {
        let isDisabled = false;
        
        if (visibilityMode === 'all') {
            // All units are enabled
            isDisabled = false;
        } else if (visibilityMode === 'dashboard') {
            // Dashboard: visibility_level 0 (Dashboard) or 2 (Both) are enabled
            isDisabled = node.visibility_level !== 0 && node.visibility_level !== 2;
        } else {
            // Frontend/Checkpoints: visibility_level 1 (Frontend) or 2 (Both) are enabled
            isDisabled = node.visibility_level !== 1 && node.visibility_level !== 2;
        }
        
        // Additionally disable leaf units (units with no children) if disableLeafUnits is true
        if (disableLeafUnits && (!node.children || node.children.length === 0)) {
            isDisabled = true;
        }
        
        options.push({
            value: node.id,
            label: node.unit_name,
            isDisabled,
            depth: flatMode ? 0 : depth,
        });
        if (node.children?.length) {
            options.push(...flattenTreeToOptions(node.children, depth + 1, visibilityMode, flatMode, disableLeafUnits));
        }
    });
    return options;
};

const UnitFilter = ({ 
    units, 
    selectedUnitIds, 
    onSelectUnits, 
    visibilityMode = 'frontend',
    flatMode = false,
    hideDisabledOptions = false,
    disabledTooltip = null,
    singleSelect = false,
    disableLeafUnits = false,
    leaderUnitIds = [],
    leaderTooltip = 'Deine Unit - hier trägst Du Handlungsfelder und Maßnahmen ein',
    deferUpdateOnClose = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    
    // For deferUpdateOnClose mode: track pending selection internally
    const [pendingSelection, setPendingSelection] = useState(null);
    const initialSelectionRef = useRef(null);

    // Build options from units hierarchy
    const options = useMemo(() => {
        const tree = buildUnitTree(units);
        let flattenedOptions = flattenTreeToOptions(tree, 0, visibilityMode, flatMode, disableLeafUnits);
        
        // Filter out disabled options if hideDisabledOptions is true
        if (hideDisabledOptions) {
            flattenedOptions = flattenedOptions.filter(opt => !opt.isDisabled);
        }
        
        return flattenedOptions;
    }, [units, visibilityMode, flatMode, hideDisabledOptions, disableLeafUnits]);

    // Get selected options for display (use effective selection in defer mode)
    const selectedOptions = useMemo(
        () => options.filter((opt) => {
            const currentSelection = deferUpdateOnClose && pendingSelection !== null 
                ? pendingSelection 
                : selectedUnitIds;
            return currentSelection?.includes(opt.value);
        }),
        [options, selectedUnitIds, deferUpdateOnClose, pendingSelection]
    );

    const leaderUnitIdSet = useMemo(() => new Set(leaderUnitIds || []), [leaderUnitIds]);

    // When dropdown opens in deferUpdateOnClose mode, store the initial selection
    useEffect(() => {
        if (deferUpdateOnClose && isOpen) {
            initialSelectionRef.current = selectedUnitIds;
            setPendingSelection(null);
        }
    }, [isOpen, deferUpdateOnClose]);

    // Handle dropdown close: apply pending selection if changed
    const handleDropdownClose = useCallback(() => {
        if (deferUpdateOnClose && pendingSelection !== null) {
            // Check if selection actually changed
            const initial = initialSelectionRef.current || [];
            const pending = pendingSelection || [];
            const hasChanged = initial.length !== pending.length || 
                initial.some((id, idx) => id !== pending[idx]);
            
            if (hasChanged) {
                onSelectUnits(pendingSelection);
            }
            setPendingSelection(null);
        }
        setIsOpen(false);
    }, [deferUpdateOnClose, pendingSelection, onSelectUnits]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                handleDropdownClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, handleDropdownClose]);

    // Close dropdown on Escape key
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isOpen) {
                handleDropdownClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleDropdownClose]);

    // Get the current effective selection (pending if in defer mode, otherwise actual)
    const effectiveSelection = useMemo(() => {
        if (deferUpdateOnClose && pendingSelection !== null) {
            return pendingSelection;
        }
        return selectedUnitIds;
    }, [deferUpdateOnClose, pendingSelection, selectedUnitIds]);

    // Toggle an option selection
    const handleOptionClick = useCallback((option) => {
        if (option.isDisabled) return;

        const currentSelection = deferUpdateOnClose && pendingSelection !== null 
            ? pendingSelection 
            : selectedUnitIds;
        const isSelected = currentSelection?.includes(option.value);
        let newSelection;

        if (singleSelect) {
            // Single-select mode: replace selection (or deselect if clicking same option)
            if (isSelected) {
                newSelection = [];
            } else {
                newSelection = [option.value];
            }
            
            if (deferUpdateOnClose) {
                // In defer mode: store pending selection, keep dropdown open
                setPendingSelection(newSelection);
            } else {
                // Normal mode: close dropdown and apply immediately
                setIsOpen(false);
                onSelectUnits(newSelection);
            }
        } else {
            // Multi-select mode: toggle selection
            if (isSelected) {
                // Remove from selection
                newSelection = currentSelection.filter((id) => id !== option.value);
            } else {
                // Add to selection
                newSelection = [...(currentSelection || []), option.value];
            }
            
            if (deferUpdateOnClose) {
                // In defer mode: store pending selection
                setPendingSelection(newSelection);
            } else {
                // Normal mode: apply immediately
                onSelectUnits(newSelection);
            }
        }
    }, [selectedUnitIds, onSelectUnits, singleSelect, deferUpdateOnClose, pendingSelection]);

    // Remove a selected unit
    const handleRemoveUnit = useCallback((e, unitId) => {
        e.stopPropagation();
        const currentSelection = deferUpdateOnClose && pendingSelection !== null 
            ? pendingSelection 
            : selectedUnitIds;
        const newSelection = currentSelection.filter((id) => id !== unitId);
        
        if (deferUpdateOnClose) {
            setPendingSelection(newSelection);
        } else {
            onSelectUnits(newSelection);
        }
    }, [selectedUnitIds, onSelectUnits, deferUpdateOnClose, pendingSelection]);

    // Toggle dropdown
    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div className="unit-filter-container" ref={containerRef}>
            {/* Control / Header */}
            <div
                className={`unit-filter-control ${isOpen ? 'is-focused' : ''}`}
                onClick={handleToggle}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
            >
                {/* Selected pills or placeholder */}
                <div className="unit-filter-content">
                    {selectedOptions.length > 0 ? (
                        selectedOptions.map((option) => (
                            <div key={option.value} className="unit-filter-pill">
                                <span>{option.label}</span>
                                <button
                                    type="button"
                                    className="unit-filter-pill-remove"
                                    onClick={(e) => handleRemoveUnit(e, option.value)}
                                    aria-label={`${option.label} entfernen`}
                                >
                                    <IconX size={18} stroke={2} />
                                </button>
                            </div>
                        ))
                    ) : (
                        <span className="unit-filter-placeholder">FILTERN NACH TEAM</span>
                    )}
                </div>

                {/* Divider */}
                <div className="unit-filter-divider" />

                {/* Dropdown indicator */}
                <div className={`unit-filter-indicator ${isOpen ? 'is-open' : ''}`}>
                    <IconChevronDown size={24} stroke={2} />
                </div>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="unit-filter-menu" role="listbox" aria-multiselectable="true">
                    {options.map((option) => {
                        const isSelected = effectiveSelection?.includes(option.value);
                        const showTooltip = option.isDisabled && disabledTooltip;
                        
                        return (
                            <div
                                key={option.value}
                                className={`unit-filter-option ${isSelected ? 'is-selected' : ''} ${option.isDisabled ? 'is-disabled' : ''} ${showTooltip ? 'has-tooltip' : ''}`}
                                onClick={() => handleOptionClick(option)}
                                role="option"
                                aria-selected={isSelected}
                                aria-disabled={option.isDisabled}
                                style={{ paddingLeft: `${20 + (option.depth || 0) * 24}px` }}
                            >
                                <div className="unit-filter-checkbox">
                                    <IconCheck size={16} stroke={3} />
                                </div>
                                <span className="unit-filter-option-label">{option.label}</span>
                                {leaderUnitIdSet.has(option.value) && !option.isDisabled && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id={`leader-tooltip-${option.value}`}>
                                                {leaderTooltip}
                                            </Tooltip>
                                        }
                                    >
                                        <span className="unit-filter-leader-icon-wrapper">
                                            <img
                                                src={leaderIcon}
                                                alt="Einheitsleitung"
                                                className="unit-filter-leader-icon"
                                            />
                                        </span>
                                    </OverlayTrigger>
                                )}
                                {showTooltip && (
                                    <span className="unit-filter-tooltip">{disabledTooltip}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

UnitFilter.propTypes = {
    units: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            unit_name: PropTypes.string.isRequired,
            level: PropTypes.number.isRequired,
            parent_unit: PropTypes.string,
            visibility_level: PropTypes.number.isRequired,
        })
    ).isRequired,
    selectedUnitIds: PropTypes.arrayOf(PropTypes.string),
    onSelectUnits: PropTypes.func.isRequired,
    // 'frontend' = visibility 1 or 2, 'dashboard' = visibility 0 or 2, 'all' = all enabled
    visibilityMode: PropTypes.oneOf(['frontend', 'dashboard', 'all']),
    // If true, all options are displayed at the same level (no hierarchy indentation)
    flatMode: PropTypes.bool,
    // If true, disabled options are hidden entirely instead of shown as disabled
    hideDisabledOptions: PropTypes.bool,
    // Tooltip text to show for disabled options (only shown if provided)
    disabledTooltip: PropTypes.string,
    // If true, only one unit can be selected at a time
    singleSelect: PropTypes.bool,
    // If true, units with no children (leaf units) are disabled
    disableLeafUnits: PropTypes.bool,
    // Unit IDs that the logged-in user leads (show icon next to these)
    leaderUnitIds: PropTypes.arrayOf(PropTypes.string),
    // Tooltip text for leader icon
    leaderTooltip: PropTypes.string,
    // If true, selection changes are deferred until dropdown closes (useful for expensive re-renders)
    deferUpdateOnClose: PropTypes.bool,
};

export default UnitFilter;
